import {
  HANDBOOK_EARLY_READER_DETAIL,
  type HandbookCount,
  type HandbookCountEvent
} from '@paction-2.0/util/constants/handbookFunnelCounts';

import { trackFunnelEvent, type FunnelEventParams } from '@/lib/analytics';

import { sendHandbookCount } from './handbookEventsTransport';

type PendingCount = Omit<HandbookCount, 'staff'>;

const MAX_PENDING_COUNTS = 50;

let viewer: { ready: boolean; staff: boolean; countingDisabled: boolean } = {
  ready: false,
  staff: false,
  countingDisabled: false
};
let pendingCounts: PendingCount[] = [];

const detailFor = (
  event: HandbookCountEvent,
  params: FunnelEventParams
): string => {
  switch (event) {
    case 'handbook_ad_view':
      return params.owned
        ? 'owned'
        : params.cta === 'claim'
          ? 'early_reader'
          : '';
    case 'handbook_feedback_submit':
      return params.count === undefined ? '' : String(params.count);
    case 'handbook_product_view':
      return params.signed_in ? 'signed_in' : 'signed_out';
    case 'handbook_checkout_start':
      return params.discounted
        ? 'additional_breed'
        : params.signed_in
          ? 'signed_in'
          : 'signed_out';
    case 'handbook_purchase':
      return params.discounted ? 'additional_breed' : '';
    case 'handbook_pay_press':
      return params.signed_in ? 'signed_in' : 'signed_out';
    case 'handbook_step_back':
      return params.step === undefined ? '' : String(params.step);
    case 'handbook_personalize_start':
      return params.early_reader
        ? HANDBOOK_EARLY_READER_DETAIL
        : (params.entry ?? '');
    case 'handbook_personalize_complete':
    case 'handbook_owned_view':
    case 'handbook_pdf_download_start':
    case 'handbook_pdf_download_complete':
      return params.early_reader ? HANDBOOK_EARLY_READER_DETAIL : '';
    case 'handbook_answers_update':
      return params.section ?? '';
    default:
      return (
        params.reason ?? params.cta ?? params.position ?? params.entry ?? ''
      );
  }
};

export const toHandbookCount = (
  event: HandbookCountEvent,
  params: FunnelEventParams = {}
): PendingCount => ({
  event,
  breedSlug: params.breed_slug ?? '',
  surface: params.surface ?? '',
  detail: detailFor(event, params)
});

export const setHandbookEventsViewer = (next: {
  staff: boolean;
  countingDisabled: boolean;
}) => {
  viewer = { ready: true, ...next };
  const queued = pendingCounts;
  pendingCounts = [];
  if (viewer.countingDisabled) return;
  for (const count of queued) {
    sendHandbookCount({ ...count, staff: viewer.staff });
  }
};

export const resetHandbookEventsViewer = () => {
  viewer = { ready: false, staff: false, countingDisabled: false };
  pendingCounts = [];
};

export const trackHandbookEvent = (
  event: HandbookCountEvent,
  params: FunnelEventParams = {}
) => {
  trackFunnelEvent(event, params);
  const count = toHandbookCount(event, params);
  if (!viewer.ready) {
    pendingCounts = [...pendingCounts, count].slice(-MAX_PENDING_COUNTS);
    return;
  }
  if (viewer.countingDisabled) return;
  sendHandbookCount({ ...count, staff: viewer.staff });
};
